import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import './TrackingPage.css';
import { toast } from 'react-toastify';
import useFleetGoogleMaps from '../utils/useFleetGoogleMaps';
import TruckIconImg from '../assets/truckicon.png';

// Google Maps API key is retrieved via useFleetGoogleMaps

const toStampUTC = (dt) => {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth()+1).padStart(2,'0');
  const d = String(dt.getUTCDate()).padStart(2,'0');
  const hh = String(dt.getUTCHours()).padStart(2,'0');
  const mm = String(dt.getUTCMinutes()).padStart(2,'0');
  const ss = String(dt.getUTCSeconds()).padStart(2,'0');
  return `${y}${m}${d}${hh}${mm}${ss}`;
};

const toStampFromISTInput = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) return '';
  const [, year, month, day, hour, minute, second = '00'] = match;
  const utcMs = Date.UTC(
    Number(year), Number(month) - 1, Number(day),
    Number(hour), Number(minute), Number(second)
  ) - 330 * 60 * 1000;
  return toStampUTC(new Date(utcMs));
};

const getClusterColor = (count) => {
  if (count <= 9) return '#4285f4';
  if (count <= 90) return '#fbbc04';
  if (count <= 499) return '#f57c00';
  return '#db4437';
};

const MAX_UNINTERPOLATED_ROUTE_GAP_METERS = 250;
const MAX_ROAD_ROUTE_BRIDGE_METERS = 2000;

const getDistanceMeters = (a, b) => {
  const radians = degrees => degrees * Math.PI / 180;
  const latDifference = radians(b.lat - a.lat);
  const lngDifference = radians(b.lng - a.lng);
  const haversine =
    Math.sin(latDifference / 2) ** 2 +
    Math.cos(radians(a.lat)) * Math.cos(radians(b.lat)) *
    Math.sin(lngDifference / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const fmtRange = (preset) => {
    // Calculate duration based on preset
    let durationMs = 4 * 60 * 60 * 1000; // Default fallback (4h)
    if (preset === '4h')  durationMs = 4 * 60 * 60 * 1000;
    if (preset === 'recent') durationMs = 30 * 24 * 60 * 60 * 1000; // Look back 30 days for current location to be safe
    if (preset === '24h') durationMs = 24 * 60 * 60 * 1000;
    if (preset === '7d') durationMs = 7 * 24 * 60 * 60 * 1000;
    if (preset === '30d') durationMs = 30 * 24 * 60 * 60 * 1000;
  
    const nowMs = Date.now();
    const startMs = nowMs - durationMs;
  
    const start = new Date(startMs);
    const now = new Date(nowMs);
  
    // Filter in UTC
    return `${toStampUTC(start)},${toStampUTC(now)}`;
  };

const toISTString = (s) => {
  if (!s) return '-';
  const str = String(s);
  if (str.length < 14) return '-';
  const y = str.slice(0,4), m = str.slice(4,6), d = str.slice(6,8), hh = str.slice(8,10), mm = str.slice(10,12), ss = str.slice(12,14);
  // Input is UTC, convert to Date object
  const date = new Date(Date.UTC(Number(y), Number(m)-1, Number(d), Number(hh), Number(mm), Number(ss)));
  // Show in IST
  return new Intl.DateTimeFormat('en-GB', { 
    timeZone: 'Asia/Kolkata', 
    year: 'numeric', month: '2-digit', day: '2-digit', 
    hour: '2-digit', minute: '2-digit', second: '2-digit', 
    hour12: false 
  }).format(date);
};

export default function TrackingPage({ imei, onBack }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const mapInitializationRef = useRef(null);
  const markersRef = useRef([]);
  const markerClustererRef = useRef(null);
  const polylineSegmentsRef = useRef([]);
  const routeGlowSegmentsRef = useRef([]);
  const routePointCountRef = useRef(0);
  const hasFitBoundsRef = useRef(false);
  const renderQueueRef = useRef([]);
  const renderFrameRef = useRef(null);
  const renderGenerationRef = useRef(0);
  const fetchCompleteRef = useRef(false);
  const roadsRequestsInFlightRef = useRef(0);
  const roadsRequestQueueRef = useRef([]);
  const infoWindowsRef = useRef([]);
  const openInfoWindowMarkersRef = useRef(new Set());
  const dropdownRef = useRef(null);
  const [preset, setPreset] = useState('recent');
  const [showDropdown, setShowDropdown] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [tempStart, setTempStart] = useState('');
  const [tempEnd, setTempEnd] = useState('');
  const [mapType, setMapType] = useState('roadmap');
  const [loading, setLoading] = useState(false);
  const [plotProgress, setPlotProgress] = useState(null);
  const [latestPoint, setLatestPoint] = useState(null);
  const [error, setError] = useState('');
  const apiKey = useMemo(() => (process.env.REACT_APP_API_KEY || (window && window.REACT_APP_API_KEY) || ''), []);

  const trackApiBase = process.env.REACT_APP_TRACK_API_BASE || 'https://mo4v7gedrd.execute-api.ap-southeast-1.amazonaws.com/testing3';
  const apiEndpoint = `${trackApiBase.replace(/\/+$/, '')}/testing3`;
  const currentLocationEndpoint = process.env.REACT_APP_TRACKCURRENT_API_BASE;

  const { isLoaded: mapsLoaded, loadError: mapsLoadError, googleMapsApiKey } = useFleetGoogleMaps();

  const addMarkerInfoWindow = (marker, content, openInitially = false) => {
    const infoWindow = new window.google.maps.InfoWindow({ content });
    infoWindowsRef.current.push(infoWindow);

    infoWindow.addListener('closeclick', () => {
      openInfoWindowMarkersRef.current.delete(marker);
    });
    marker.addListener('click', () => {
      if (openInfoWindowMarkersRef.current.has(marker)) {
        infoWindow.close();
        openInfoWindowMarkersRef.current.delete(marker);
      } else {
        infoWindow.open({ anchor: marker, map: mapInstance.current });
        openInfoWindowMarkersRef.current.add(marker);
      }
    });
    if (openInitially) {
      infoWindow.open({ anchor: marker, map: mapInstance.current });
      openInfoWindowMarkersRef.current.add(marker);
    }
  };

  const getAddress = (lat, lng) => {
    return new Promise((resolve) => {
      if (!window.google || !window.google.maps) {
        resolve('');
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          resolve(results[0].formatted_address);
        } else {
          resolve(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        }
      });
    });
  };

  const initMap = useCallback(async () => {
    if (!window.google || !window.google.maps) return;
    if (mapInstance.current) return;
    if (mapInitializationRef.current) return mapInitializationRef.current;

    const gm = window.google.maps;

    mapInitializationRef.current = (async () => {
      let MapConstructor = gm.Map;

      try {
        if (gm.importLibrary) {
          const mapsLib = await gm.importLibrary("maps");
          if (mapsLib && mapsLib.Map) {
            MapConstructor = mapsLib.Map;
          }
          await gm.importLibrary("marker");
        }
      } catch (e) {
        console.warn("Failed to load libraries", e);
      }

      if (!MapConstructor) {
        console.error("Google Maps Map constructor not found");
        return null;
      }

      if (!mapInstance.current && mapRef.current) {
        mapInstance.current = new MapConstructor(mapRef.current, {
          center: { lat: 20.5937, lng: 78.9629 }, // Default to India
          zoom: 5,
          mapTypeId: mapType,
          disableDefaultUI: true, // Remove default UI (Map/Satellite text)
          attributionControl: false, // Remove attribution control
          gestureHandling: 'greedy', // Improves touch/scroll smoothness
        });

        mapInstance.current.addListener('click', () => {
          infoWindowsRef.current.forEach(infoWindow => infoWindow.close());
          openInfoWindowMarkersRef.current.clear();
        });
      }
      return mapInstance.current;
    })();

    try {
      await mapInitializationRef.current;
    } finally {
      mapInitializationRef.current = null;
    }
  }, [mapType]);

  useEffect(() => {
    let cancelled = false;
    if (!mapsLoaded) {
      if (mapsLoadError) {
        if (!cancelled) console.error('Failed to load Google Maps');
      }
      return () => { cancelled = true; };
    }

    requestAnimationFrame(() => {
      if (!cancelled) initMap();
    });
    return () => { cancelled = true; };
  }, [mapsLoaded, mapsLoadError, initMap]);

  const fetchPoints = async (range, endpoint = apiEndpoint, onPage, signal) => {
    // setLoading(true); // Handled by parent
    setError('');
    try {
      const isCurrentOnly = Boolean(currentLocationEndpoint) && endpoint === currentLocationEndpoint;
      const mappedRecords = [];
      const requestedTokens = new Set();
      let nextToken = null;
      let hasMore = true;
      let page = 0;
      let paginationStoppedEarly = false;

      while (hasMore) {
        const query = new URLSearchParams({ imeis: imei, limit: '1000' });
        if (!isCurrentOnly) query.set('TimeRange', range);
        if (nextToken !== null) query.set('nextToken', nextToken);

        const res = await fetch(`${endpoint}?${query.toString()}`, {
          headers: apiKey ? { 'x-api-key': apiKey } : undefined,
          signal
        });
        if (res.status === 404) {
          toast.info('No data present between this time range');
          return [];
        }
        if (!res.ok) {
          const text = await res.text();
          toast.error(`Server error ${res.status}: ${text.slice(0,120)}`);
          return [];
        }

        const json = await res.json();
        let payload = json;
        if (typeof payload?.body === 'string') {
          try { payload = JSON.parse(payload.body); } catch { /* ignore */ }
        }
        const pageRecords = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
        page += 1;
        const mappedPage = [];
        pageRecords.forEach((record) => {
          if (!record || (!isCurrentOnly && (
            record.GPS === false || String(record.GPS).toLowerCase() === 'false'
          ))) {
            return;
          }
          if (record.latitude == null || record.longitude == null) {
            return;
          }
          const lat = Number(record.latitude);
          const lng = Number(record.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            return;
          }
          const point = {
            imei: record.IMEI || imei,
            ts: record.time_stamp,
            lat,
            lng,
            speed: record.speed || 0
          };
          mappedRecords.push(point);
          mappedPage.push(point);
        });
        mappedPage.sort((a, b) => String(a.ts).localeCompare(String(b.ts)));
        hasMore = payload?.hasMore === true;
        if (onPage) onPage(mappedPage, { page, hasMore });

        if (hasMore) {
          const token = payload?.nextToken;
          if (!token) {
            paginationStoppedEarly = true;
            break;
          }
          if (requestedTokens.has(token)) {
            paginationStoppedEarly = true;
            break;
          }
          requestedTokens.add(token);
          nextToken = token;
        }
      }

      if (paginationStoppedEarly && onPage) await onPage([], { isLastPage: true });
      const mapped = mappedRecords.sort((a, b) => String(a.ts).localeCompare(String(b.ts)));
      if (!mapped.length) {
        toast.info('No data present between this time range');
      }
      return mapped;
    } catch (e) {
      if (e.name === 'AbortError') return [];
      console.error(e);
      setError('Failed to fetch points');
      toast.error('Failed to fetch location points from server');
      return [];
    } finally {
      // setLoading(false); // Handled by parent
    }
  };

  const fetchRoadChunk = (url, signal) => new Promise((resolve, reject) => {
    const startRequest = () => {
      if (signal.aborted) {
        const error = new Error('Road snapping request was cancelled');
        error.name = 'AbortError';
        reject(error);
        return;
      }

      roadsRequestsInFlightRef.current += 1;
      fetch(url, { signal }).then(resolve, reject).finally(() => {
        roadsRequestsInFlightRef.current -= 1;
        const nextRequest = roadsRequestQueueRef.current.shift();
        if (nextRequest) nextRequest();
      });
    };

    if (roadsRequestsInFlightRef.current < 3) {
      startRequest();
    } else {
      roadsRequestQueueRef.current.push(startRequest);
    }
  });

  const snapRoutePoints = async (points, signal) => {
    const chunkSize = 100;
    const chunkStride = chunkSize - 1;
    const routeSegments = [];
    const chunks = [];

    for (let start = 0; start < points.length; start += chunkStride) {
      chunks.push(points.slice(start, start + chunkSize));
    }

    for (let offset = 0; offset < chunks.length; offset += 3) {
      const snappedChunks = await Promise.all(
        chunks.slice(offset, offset + 3).map(async chunk => {
          const path = chunk.map(point => `${point.lat},${point.lng}`).join('|');
          const url = `https://roads.googleapis.com/v1/snapToRoads?interpolate=true&path=${encodeURIComponent(path)}&key=${googleMapsApiKey}`;
          const response = await fetchRoadChunk(url, signal);
          if (!response.ok) {
            throw new Error(`Roads API returned ${response.status}`);
          }

          const result = await response.json();
          if (result?.error) {
            throw new Error(result.error.message || 'Roads API request failed');
          }
          return Array.isArray(result?.snappedPoints) ? result.snappedPoints : [];
        })
      );

      snappedChunks.forEach((snappedPoints) => {
        let currentSegment = [];
        let betweenAnchors = [];
        let previousOriginalIndex = null;
        const chunkSegments = [];
        const appendIfDistinct = (coordinate) => {
          const last = currentSegment[currentSegment.length - 1];
          if (
            !last ||
            Math.abs(last.lat - coordinate.lat) >= 1e-7 ||
            Math.abs(last.lng - coordinate.lng) >= 1e-7
          ) {
            currentSegment.push(coordinate);
          }
        };

        snappedPoints.forEach((point) => {
          const lat = Number(point.location?.latitude);
          const lng = Number(point.location?.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

          const coordinate = { lat, lng };
          const originalIndex = point.originalIndex == null
            ? Number.NaN
            : Number(point.originalIndex);
          if (Number.isInteger(originalIndex)) {
            if (previousOriginalIndex === null) {
              currentSegment = [coordinate];
            } else if (originalIndex === previousOriginalIndex + 1) {
              const previousCoordinate = currentSegment[currentSegment.length - 1];
              const missingRoadGeometry =
                betweenAnchors.length === 0 &&
                previousCoordinate &&
                getDistanceMeters(previousCoordinate, coordinate) > MAX_UNINTERPOLATED_ROUTE_GAP_METERS;
              if (missingRoadGeometry) {
                if (currentSegment.length > 1) chunkSegments.push(currentSegment);
                currentSegment = [coordinate];
              } else {
                betweenAnchors.forEach(appendIfDistinct);
                appendIfDistinct(coordinate);
              }
            } else {
              if (currentSegment.length > 1) chunkSegments.push(currentSegment);
              currentSegment = [coordinate];
            }
            betweenAnchors = [];
            previousOriginalIndex = originalIndex;
          } else if (previousOriginalIndex !== null) {
            betweenAnchors.push(coordinate);
          }
        });

        if (currentSegment.length > 1) chunkSegments.push(currentSegment);

        chunkSegments.forEach((segment) => {
          const previousSegment = routeSegments[routeSegments.length - 1];
          const first = segment[0];
          const last = previousSegment?.[previousSegment.length - 1];
          if (
            last &&
            Math.abs(last.lat - first.lat) < 1e-7 &&
            Math.abs(last.lng - first.lng) < 1e-7
          ) {
            previousSegment.push(...segment.slice(1));
          } else {
            routeSegments.push(segment);
          }
        });
      });
    }

    const connectedSegments = [];
    let unbridgedGaps = 0;
    const directionsService = window.google?.maps?.DirectionsService
      ? new window.google.maps.DirectionsService()
      : null;

    for (const segment of routeSegments) {
      const previousSegment = connectedSegments[connectedSegments.length - 1];
      if (!previousSegment) {
        connectedSegments.push(segment);
        continue;
      }

      const from = previousSegment[previousSegment.length - 1];
      const to = segment[0];
      const gapMeters = getDistanceMeters(from, to);
      if (gapMeters < 3) {
        previousSegment.push(...segment.slice(1));
        continue;
      }

      let roadBridge = null;
      if (directionsService && gapMeters <= MAX_ROAD_ROUTE_BRIDGE_METERS && !signal.aborted) {
        const result = await new Promise((resolve) => {
          directionsService.route(
            {
              origin: from,
              destination: to,
              travelMode: window.google.maps.TravelMode.DRIVING
            },
            (directions, status) => {
              const path = status === window.google.maps.DirectionsStatus.OK
                ? directions?.routes?.[0]?.overview_path
                : null;
              resolve(Array.isArray(path) && path.length > 1
                ? path.map(point => ({ lat: point.lat(), lng: point.lng() }))
                : null);
            }
          );
        });
        if (signal.aborted) return { segments: [], unbridgedGaps: 0 };
        roadBridge = result;
      }

      if (roadBridge) {
        previousSegment.push(...roadBridge.slice(1, -1), ...segment);
      } else {
        connectedSegments.push(segment);
        unbridgedGaps += 1;
      }
    }

    return { segments: connectedSegments, unbridgedGaps };
  };

  const clearMap = () => {
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
      markerClustererRef.current = null;
    }
    infoWindowsRef.current.forEach(infoWindow => infoWindow.close());
    infoWindowsRef.current = [];
    openInfoWindowMarkersRef.current.clear();
    if (markersRef.current?.length) {
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
    }
    polylineSegmentsRef.current.forEach(polyline => polyline.setMap(null));
    polylineSegmentsRef.current = [];
    routeGlowSegmentsRef.current = [];
    routePointCountRef.current = 0;
  };

  const renderRouteEndpoints = (points) => {
    if (!window.google?.maps || !mapInstance.current || !points.length) return;

    const gm = window.google.maps;
    const first = points[0];
    const last = points[points.length - 1];
    const bounds = new gm.LatLngBounds();

    points.forEach(point => bounds.extend({ lat: point.lat, lng: point.lng }));

    const startIconSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="128" height="76" viewBox="0 0 128 76">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="150%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#0f172a" flood-opacity=".3"/>
          </filter>
        </defs>
        <g filter="url(#shadow)">
          <path d="M29 5C14 5 4 16 4 30c0 18 25 41 25 41s25-23 25-41C54 16 44 5 29 5Z" fill="#15803d" stroke="#fff" stroke-width="3"/>
          <circle cx="29" cy="29" r="15" fill="#fff"/>
          <text x="29" y="35" text-anchor="middle" font-family="Arial,sans-serif" font-size="17" font-weight="700" fill="#15803d">S</text>
          <rect x="46" y="16" width="78" height="30" rx="15" fill="#15803d" stroke="#fff" stroke-width="2"/>
          <text x="85" y="36" text-anchor="middle" font-family="Arial,sans-serif" font-size="14" font-weight="700" letter-spacing=".5" fill="#fff">START</text>
        </g>
      </svg>`;
    const startIcon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(startIconSvg)}`,
      scaledSize: new gm.Size(128, 76),
      anchor: new gm.Point(29, 69)
    };
    const endIcon = {
      url: TruckIconImg,
      scaledSize: new gm.Size(96, 96),
      anchor: new gm.Point(48, 48)
    };

    const addEndpointMarker = (point, title, icon, zIndex) => {
      const marker = new gm.Marker({
        position: { lat: point.lat, lng: point.lng },
        map: mapInstance.current,
        title,
        icon,
        zIndex
      });

      const content = `<div>
            <b>${title}</b><br/>
            <b>Time:</b> ${toISTString(point.ts)}<br/>
            <b>Lat:</b> ${point.lat.toFixed(6)}<br/>
            <b>Long:</b> ${point.lng.toFixed(6)}
          </div>`;
      addMarkerInfoWindow(marker, content);

      markersRef.current.push(marker);
    };

    addEndpointMarker(first, 'Start point', startIcon, 2000);
    addEndpointMarker(last, 'End point', endIcon, 2001);

    if (!bounds.isEmpty()) {
      mapInstance.current.fitBounds(bounds);
      if (points.length === 1) mapInstance.current.setZoom(15);
      hasFitBoundsRef.current = true;
    }
  };

  const renderMap = (origPoints, drawMarkers = true, fitBounds = false, renderClusters = true, drawRoute = true) => {
    if (!window.google || !window.google.maps) return false;
    const gm = window.google.maps;
    if (!mapInstance.current) return false;
    
    const bounds = new gm.LatLngBounds();
    // Draw logic based on mode
    if (preset === 'recent') {
      // MODE: Current Location (Most Recent)
      // Show ONLY the Truck Icon at the latest location. NO Route. NO other markers.
      
      const sortedPoints = [...origPoints].sort((a, b) => String(b.ts).localeCompare(String(a.ts)));
      const latest = sortedPoints[0];

      if (latest) {
        const pos = { lat: latest.lat, lng: latest.lng };
        const contentString = `<div style="min-width:180px">
              <b>Latest Location</b><br/>
              <b>IMEI:</b> ${latest.imei}<br/>
              <b>Time (IST):</b> ${toISTString(latest.ts)}<br/>
              <b>Coords:</b> ${latest.lat.toFixed(6)}, ${latest.lng.toFixed(6)}<br/>
              <b>Address:</b> ${latest.address || 'Loading...'}
            </div>`;

        // Use Legacy Marker to ensure custom styles work (AdvancedMarkerElement requires Map ID which overrides styles)
        const marker = new gm.Marker({
            position: pos,
            map: mapInstance.current,
            title: `${imei} - ${latest.ts}`,
            icon: {
                url: TruckIconImg,
                scaledSize: new gm.Size(96, 96),
                anchor: new gm.Point(48, 48)
            },
            zIndex: 999
        });
        
        addMarkerInfoWindow(marker, contentString, true);
        
        markersRef.current.push(marker);
          
        bounds.extend(pos);
      }
    } else {
      // MODE: History (4h, 24h, etc.)
      // Show Route (Polyline) + Standard Markers (Pins). NO Truck Icon.

      const routePoints = drawRoute ? origPoints : [];
      if (drawRoute && routePoints.length > 1) {
        if (routePointCountRef.current + origPoints.length <= 5000) {
          const routeGlow = new gm.Polyline({
            path: routePoints,
            strokeColor: '#0088ff',
            strokeOpacity: 0.2,
            strokeWeight: 8,
            map: mapInstance.current,
            geodesic: false
          });
          polylineSegmentsRef.current.push(routeGlow);
          routeGlowSegmentsRef.current.push(routeGlow);
        }
        const routePolyline = new gm.Polyline({
          path: routePoints,
          strokeColor: '#0088ff',
          strokeOpacity: 1,
          strokeWeight: 4,
          map: mapInstance.current,
          geodesic: false
        });
        polylineSegmentsRef.current.push(routePolyline);
      }
      if (drawRoute && origPoints.length) {
        routePointCountRef.current += origPoints.length;
        if (routePointCountRef.current > 5000 && routeGlowSegmentsRef.current.length) {
          routeGlowSegmentsRef.current.forEach(polyline => polyline.setMap(null));
          const glowSegments = new Set(routeGlowSegmentsRef.current);
          polylineSegmentsRef.current = polylineSegmentsRef.current.filter(polyline => !glowSegments.has(polyline));
          routeGlowSegmentsRef.current = [];
        }
      }

      // 2. Draw Standard Markers for all points
      const newMarkers = [];
      if (drawMarkers) origPoints.forEach((p) => {
        const pos = { lat: p.lat, lng: p.lng };
        bounds.extend(pos);
        
        // Use Legacy Marker for styles compatibility
        const marker = new gm.Marker({
            position: pos,
            title: `${imei} - ${p.ts}`
        });
        
        const content = `<div>
                <b>Time:</b> ${toISTString(p.ts)}<br/>
                <b>Lat:</b> ${p.lat.toFixed(6)}<br/>
                <b>Long:</b> ${p.lng.toFixed(6)}
              </div>`;
        addMarkerInfoWindow(marker, content);
        
        markersRef.current.push(marker);
        newMarkers.push(marker);
      });

      if (newMarkers.length) {
        if (!markerClustererRef.current) {
          markerClustererRef.current = new MarkerClusterer({
            map: mapInstance.current,
            algorithm: new SuperClusterAlgorithm({ radius: 60, maxZoom: 20 }),
            renderer: {
              render: ({ count, position }) => {
                const color = getClusterColor(count);
                const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52"><circle cx="26" cy="26" r="23" fill="${color}" stroke="#fff" stroke-width="3"/></svg>`;
                return new gm.Marker({
                  position,
                  icon: {
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
                    scaledSize: new gm.Size(42, 42)
                  },
                  label: {
                    text: String(count),
                    color: count >= 10 && count <= 90 ? '#202124' : '#fff',
                    fontSize: '12px',
                    fontWeight: '700'
                  },
                  title: `${count} telemetry points`,
                  zIndex: 1000 + count
                });
              }
            }
          });
        }
        markerClustererRef.current.addMarkers(newMarkers, true);
      }
      if (renderClusters && markerClustererRef.current) {
        markerClustererRef.current.render();
      }

    }
    
    if (fitBounds && !hasFitBoundsRef.current && !bounds.isEmpty()) {
       mapInstance.current.fitBounds(bounds);
       // If only one point (recent mode), zoom out a bit
       if (markersRef.current.length === 1) {
          mapInstance.current.setZoom(15);
       }
       hasFitBoundsRef.current = true;
    }
    return true;
  };

  useEffect(() => {
    let cancelled = false;
    const generation = ++renderGenerationRef.current;
    const requestController = new AbortController();
    const doLoad = async () => {
      if (preset === 'custom' && (!customStart || !customEnd)) return;
      if (!mapsLoaded && !(window.google && window.google.maps)) return;
      setLoading(true);
      setPlotProgress(null);
      clearMap();
      hasFitBoundsRef.current = false;
      renderQueueRef.current = [];
      fetchCompleteRef.current = false;
      roadsRequestQueueRef.current = [];
      if (renderFrameRef.current !== null) {
        cancelAnimationFrame(renderFrameRef.current);
        renderFrameRef.current = null;
      }
      try {
        if (window.google && window.google.maps) {
          await initMap();
        } else if (mapsLoaded) {
          await initMap();
        }

        let range;
        if (preset === 'custom') {
          range = `${toStampFromISTInput(customStart)},${toStampFromISTInput(customEnd)}`;
        } else {
          range = fmtRange(preset);
        }

        const endpoint = preset === 'recent' && currentLocationEndpoint ? currentLocationEndpoint : apiEndpoint;
        const enqueueRouteSegments = (segments) => {
          segments.forEach((segment) => {
            if (segment.length < 2) return;
            renderQueueRef.current.push({
              points: segment,
              offset: 0,
              drawMarkers: false,
              drawRoute: true
            });
          });
          scheduleRender();
        };
        const scheduleRender = () => {
          if (cancelled || generation !== renderGenerationRef.current || renderFrameRef.current !== null) return;
          renderFrameRef.current = requestAnimationFrame(() => {
            renderFrameRef.current = null;
            if (cancelled || generation !== renderGenerationRef.current) return;

            const job = renderQueueRef.current[0];
            if (!job) {
              if (fetchCompleteRef.current) {
                setLoading(false);
                setPlotProgress(null);
              }
              return;
            }

            const batch = job.drawRoute
              ? job.points
              : job.points.slice(job.offset, job.offset + 250);
            job.offset += batch.length;
            const pageComplete = job.offset >= job.points.length;
            try {
              renderMap(
                batch,
                job.drawMarkers,
                !hasFitBoundsRef.current && job.drawMarkers,
                pageComplete && job.drawMarkers,
                job.drawRoute
              );
              if (pageComplete) {
                renderQueueRef.current.shift();
                setLoading(false);
                if (job.drawMarkers) {
                  setPlotProgress(count => (count || 0) + job.points.length);
                }
              }
              scheduleRender();
            } catch (error) {
              console.error('Failed to render tracking points', error);
              setError('Failed to render tracking data');
              toast.error('Failed to render tracking data');
              renderQueueRef.current = [];
              setLoading(false);
              setPlotProgress(null);
            }
          });
        };

        const pts = await fetchPoints(range, endpoint, (pagePoints) => {
          if (cancelled || preset === 'recent') return;
          if (!pagePoints.length) return;
          renderQueueRef.current.push({
            points: pagePoints,
            offset: 0,
            drawMarkers: true,
            drawRoute: false
          });
          scheduleRender();
        }, requestController.signal);
        if (cancelled) return;

        fetchCompleteRef.current = true;
        if (preset !== 'recent' && pts.length > 1) {
          try {
            const { segments, unbridgedGaps } = await snapRoutePoints(pts, requestController.signal);
            if (cancelled) return;
            enqueueRouteSegments(segments);
            if (unbridgedGaps > 0) {
              toast.info('Some route gaps could not be connected by road and remain unconnected.');
            }
          } catch (error) {
            if (error.name !== 'AbortError' && !cancelled) {
              console.error('Road snapping failed', error);
              setError('Failed to load road-aligned route');
              toast.error('Road-aligned route unavailable. No straight-line fallback was drawn.');
            }
          }
        }
        scheduleRender();

        if (pts.length > 0) {
          const latest = pts[pts.length - 1];
          const address = await getAddress(latest.lat, latest.lng);
          latest.address = address;
          setLatestPoint({ ...latest, address });
          if (preset !== 'recent') renderRouteEndpoints(pts);
        } else {
          setLatestPoint(null);
        }

        if (cancelled) return;
        if (preset === 'recent' && pts.length) {
          renderMap(pts, true, true, true);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError('Failed to load tracking data');
          toast.error('Failed to load tracking data');
          setLoading(false);
          setPlotProgress(null);
        }
      } finally {
        if (!cancelled && preset === 'recent') setLoading(false);
      }
    };
    doLoad();
    return () => {
      cancelled = true;
      requestController.abort();
      roadsRequestQueueRef.current = [];
      if (renderFrameRef.current !== null) {
        cancelAnimationFrame(renderFrameRef.current);
        renderFrameRef.current = null;
      }
      renderQueueRef.current = [];
      if (generation === renderGenerationRef.current) {
        renderGenerationRef.current += 1;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imei, preset, customStart, customEnd, mapsLoaded]);

  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.setMapTypeId(mapType);
      // Always use the default Google Maps styling (no custom overrides)
      mapInstance.current.setOptions({ styles: [] });
    }
  }, [mapType]);

  const onPresetChange = (p) => {
    setPreset(p);
    if (p !== 'custom') {
      setShowDropdown(false);
    }
  };

  const handleApplyCustom = () => {
    if (!tempStart || !tempEnd) {
      toast.warning('Please select both start and end time');
      return;
    }
    if (tempStart >= tempEnd) {
      toast.error('Start time must be before end time');
      return;
    }
    setCustomStart(tempStart);
    setCustomEnd(tempEnd);
    setShowDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="track-root">
      <div className="map" ref={mapRef} id="map-canvas" />
      
      <header className="track-controls">
       <button className="back-btn" onClick={onBack}>
  Back
</button>
        <div className="right-controls">
          <button className={`map-toggle-btn ${mapType === 'satellite' ? 'active' : ''}`} onClick={() => setMapType(mapType === 'roadmap' ? 'satellite' : 'roadmap')}>
            {mapType === 'roadmap' ? 'Map' : 'Satellite'}
          </button>
          <div className="calendar-container" ref={dropdownRef}>
            <button className="calendar-btn" onClick={() => setShowDropdown(!showDropdown)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Date
            </button>
            {showDropdown && (
              <div className="dropdown-menu">
                <button onClick={() => onPresetChange('recent')}>Current Location</button>
                <button onClick={() => onPresetChange('4h')}>Last 4 Hours</button>
                <button onClick={() => onPresetChange('24h')}>Last 24 Hours</button>
                <button onClick={() => onPresetChange('7d')}>Last 7 Days</button>
                <button onClick={() => onPresetChange('30d')}>Last 30 Days</button>
                <button onClick={() => onPresetChange('custom')} className={preset === 'custom' ? 'active' : ''}>Custom Range</button>
                
                {preset === 'custom' && (
                  <div className="custom-range-inputs">
                    <div className="input-group">
                      <label>Start (IST)</label>
                      <input type="datetime-local" value={tempStart} onChange={(e) => setTempStart(e.target.value)} />
                    </div>
                    <div className="input-group">
                      <label>End (IST)</label>
                      <input type="datetime-local" value={tempEnd} onChange={(e) => setTempEnd(e.target.value)} />
                    </div>
                    <button className="apply-btn" onClick={handleApplyCustom}>Apply</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {latestPoint && (
        <div className="live-status-card">
          <div className="live-status-header">
            <span className="status-dot"></span>
            <h3>Live Status</h3>
          </div>
          <div className="live-status-content">
            <div className="info-item">
               <label>SPEED</label>
               <p>{latestPoint.speed ? `${latestPoint.speed} km/h` : '0 km/h'}</p>
             </div>
            <div className="info-item">
              <label>LAST UPDATED</label>
              <p>{toISTString(latestPoint.ts)}</p>
            </div>
            <div className="info-item">
              <label>ADDRESS</label>
              <p>{latestPoint.address || 'Loading...'}</p>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="spinner-overlay" role="status" aria-live="polite">
          <div className="spinner" />
          <span>Loading data...</span>
        </div>
      )}
      {plotProgress !== null && !loading && (
        <div className="map-progress" role="status" aria-live="polite">
          Plotting {plotProgress.toLocaleString()} points...
        </div>
      )}
    </div>
  );
}
